import { DataFormats } from '@skylab-shared/constants';
import { HttpService, Query, QueryHandler, RootContextBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { ClientKind } from '@skylab-shared/dataModel';
import { MS_PER_MINUTE } from '@skylab-shared/utils';
import { format } from 'fecha';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { Address } from '../common/address';

export interface SalesByClientsParameters {
    dateFrom: Date;
    dateTo: Date;
    omitInvoiceIssuedToReceipt: boolean;
    convertToPln: boolean;
    clientKinds?: ClientKind[];
    clientGroupId?: string;
    noClientGroup?: boolean;
    search: string;
}

export interface SalesByClientsModel {
    address: Address;
    country?: string;
    baseCurrencyGross: number;
    baseCurrencyId: string;
    baseCurrencyNet: number;
    baseCurrencyVat: number;
    clientGroupId: string;
    countryId: number;
    currencyId: string;
    gross: number;
    name: string;
    net: number;
    tin: string;
    vat: number;
}

interface SalesByClientsInternalItem {
    baseCurrencyGross: number;
    baseCurrencyId: string;
    baseCurrencyNet: number;
    baseCurrencyVat: number;
    clientCity: string;
    clientGroupId: string;
    clientId: number;
    clientLine1: string;
    clientName: string;
    clientZipCode: string;
    countryId: number;
    currencyId: string;
    gross: number;
    net: number;
    tin: string;
    vat: number;
}

export class SalesByClientsCollectionQuery extends Query<SalesByClientsModel[]> {
    public constructor(public readonly parameters: SalesByClientsParameters) {
        super();
    }
}

export class SalesByClientsCollectionQueryHandlerBase implements QueryHandler<SalesByClientsCollectionQuery, SalesByClientsModel[]> {
    private static Url = TaggedUrl.join('salesByClients', [Tags.Api]);

    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase
    ) { }

    public async handleAsync(query: SalesByClientsCollectionQuery): Promise<SalesByClientsModel[]> {
        const { data } = await this.http.get<SalesByClientsInternalItem[]>(SalesByClientsCollectionQueryHandlerBase.Url, {
            params: {
                ...query.parameters,
                dateFrom: query.parameters.dateFrom && format(query.parameters.dateFrom, DataFormats.date.webApi),
                dateTo: query.parameters.dateTo && format(query.parameters.dateTo, DataFormats.date.webApi)
            },
            timeout: 3 * MS_PER_MINUTE
        });

        const countries = await this.rootContext.getAsync(CountryCollectionQuery);

        return data.map(item => ({
            address: {
                city: item.clientCity,
                countryId: item.countryId,
                country: null,
                line1: item.clientLine1,
                zipCode: item.clientZipCode
            },
            country: countries.find(elem => elem.id === item.countryId)?.name,
            baseCurrencyId: item.baseCurrencyId,
            baseCurrencyGross: item.baseCurrencyGross,
            baseCurrencyNet: item.baseCurrencyNet,
            baseCurrencyVat: item.baseCurrencyVat,
            clientGroupId: item.clientGroupId,
            currencyId: item.currencyId,
            countryId: item.countryId,
            gross: item.gross,
            name: item.clientName,
            net: item.net,
            tin: item.tin,
            vat: item.vat
        }));
    }
}
